// ── OddsBooksTable — every OddsPapi book for the selected line ──────
//
// Replaces the Unabated consensus chart in GameDetail (Dan, 2026-10-10).
// #1145 places it between Moneyline and Spread/Total. One row per side of
// the market, one column per book: the
// OddsPapi consensus first, then Pinnacle, then the field books. The table
// follows the line the trade panel is on (oddsBoard.resolveOddsBoardSelection)
// and shows the full-game moneyline when nothing else is selected — or,
// labelled, while the selected line has no OddsPapi quotes, so it is never
// empty on load.
//
// Freshness: a cell lights when its quote updates — green when the side's
// probability rose, red when it fell, grey when the book re-quoted the same
// price — and fades over the window that quote carries its fresh weight in
// the consensus math: 5 s for field books and the consensus itself
// (OP_FIELD_FRESH_MS), 6 s for Pinnacle (OP_ANCHOR_GLOW_MS). A cell that
// mounts on an already-aging quote (page open, line switch) shows only what
// is left of that window, in grey: there is no previous price to compare.
//
// Each book's header carries a last-update clock (quoteAgeLabel: 1s…5s, then
// 5s+, 10s+, 20s+, 30s+, 60s+) on the app's shared one-second clock. Pinnacle
// keeps its column even when it is not quoting.
//
// Nothing moves sideways (Dan, 2026-10-10): every column has a fixed width,
// and the field is capped at the ten most recently updated books, each
// keeping its slot (nextFieldBookSlots) — a newcomer takes over a quiet
// book's column in place.
//
// My books (#1287): a trader who picked their own books in Settings sees just
// those as columns, in a fixed order (oddsBoard.personalBookColumns), with a
// My books / All books switch in the header. A picked book that is not quoting
// reads as today; one outside hydra's reviewed census says "not in census". The
// Consensus column is hydra's, unchanged, in both views. My books adds its own
// labelled "My books" column beside it: the de-vigged consensus of only the
// picked books (lib/selectedBookConsensus, contract
// docs/261022Issue1287SelectedBookConsensus.md), worked out here for this one
// market at the shared clock's time. Display only: it never reaches the
// market store, a price, an order or any automated input. With nothing picked
// (the default) none of this runs and the table is exactly as above.

/** Column widths, in rem: side label, consensus, each book. */
const LABEL_REM = 7;
const CONSENSUS_REM = 4.75;
const BOOK_REM = 4.5;

import { useMemo, useState } from 'react';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import {
  americanForQuote,
  decimalToImpliedPct,
  decimalToImpliedPctText,
  fairAmericanText,
  fairPctText,
  formatOpAge,
  OP_ANCHOR_GLOW_MS,
  OP_FIELD_FRESH_MS,
  opWithdrawReasonLabel,
} from '@/lib/opOdds';
import {
  OP_CONSENSUS_ELIGIBLE_BOOKS,
  selectedBookConsensus,
  type SelectedBookConsensusResult,
  type SelectedBookExclusion,
} from '@/lib/selectedBookConsensus';
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useMarketStore } from '@/stores/marketStore';
import { selectOwnedPersonalBooks, usePersonalBooks } from '@/stores/personalBooks';
import { GAME_DETAIL_TABLE_FADE } from './CompactLinesTable';
import {
  FIELD_BOOK_CAP,
  nextFieldBookSlots,
  oddsBoardSides,
  oddsBoardSwapped,
  oddsBoardTitle,
  oddsBookLabel,
  personalBookColumns,
  quoteAgeLabel,
  resolveOddsBoardSlice,
  type OddsBoardSelection,
} from './oddsBoard';

type TickDir = 'up' | 'down' | 'neutral';
/** Literal names, so Tailwind's content scan keeps the @layer utilities
 *  rules (globals.css). */
const TICK_CLASS: Record<TickDir, string> = {
  up: 'animate-odds-tick-up',
  down: 'animate-odds-tick-down',
  neutral: 'animate-odds-tick-neutral',
};
interface Tick {
  value: number | null;
  stamp: number | null;
  /** What made the value besides its time (the My books contributors). */
  basis: string | undefined;
  /** The newest stamp this cell has seen: a stamp at or before it is not a
   *  newer quote (a book returning with its old quote time). */
  maxStamp: number | null;
  /** Counts highlights started. A cell with a basis remounts — restarting its
   *  one-shot animation — exactly when this goes up, so a silent follow never
   *  replays it and every highlight plays, whatever stamp/books it lands on. */
  flash: number;
  dir: TickDir;
  /** Frozen per highlight: re-deriving it from the clock on every render
   *  would re-time the running animation. */
  delayMs: number;
}

/** The highlight a cell is running. An update seen while mounted starts a
 *  full window now (provider clocks can lead or lag ours); the quote a cell
 *  mounts with starts part-way, by its own age. Derived during render, the
 *  same pattern as useChangeKey. */
function useTick(value: number | null, stamp: number | null, windowMs: number, basis?: string): Tick {
  const [tick, setTick] = useState<Tick>(() => ({
    value,
    stamp,
    basis,
    maxStamp: stamp,
    flash: 0,
    dir: 'neutral',
    delayMs: stamp == null ? 0 : -Math.min(Math.max(0, Date.now() - stamp), windowMs),
  }));
  // A new quote — or, for the My books value, a change in which books make it
  // (Pinnacle struck at 5 s, a book selected out) that moves the value — is an
  // update and lights. A change of books that leaves the value where it was,
  // with no NEWER quote than any this cell has seen (the freshest contributor
  // leaving, a book re-picked or returning with its old quote time), is
  // followed silently. A change that brings a quote newer than any this cell
  // has shown lights grey even with the value put: a book picked while it
  // quotes looks exactly like a book starting to quote (both a newer stamp
  // with new books), and grey means a same-price re-quote.
  const maxStamp = tick.maxStamp == null ? stamp : stamp == null ? tick.maxStamp : Math.max(tick.maxStamp, stamp);
  const noNewerQuote = stamp == null || (tick.maxStamp != null && stamp <= tick.maxStamp);
  if (basis !== undefined && tick.basis !== basis && tick.value === value && noNewerQuote) {
    const next = { ...tick, stamp, basis, maxStamp };
    setTick(next);
    return next;
  }
  if (tick.stamp !== stamp || tick.basis !== basis) {
    const dir: TickDir =
      value == null || tick.value == null || value === tick.value ? 'neutral' : value > tick.value ? 'up' : 'down';
    const next = { value, stamp, basis, maxStamp, flash: tick.flash + 1, dir, delayMs: 0 };
    setTick(next);
    return next;
  }
  // Same quote and books, new value: the My books consensus settling as
  // Pinnacle's arrival weight decays (#1287). Follow it without a new
  // highlight, so the next update compares against the number on screen.
  // Only for the My books cell (the one that passes a `basis`): every other
  // cell keeps the shipped rule, comparing a new quote with the value at the
  // last one — hydra's consensus can move under an unchanged lastUpdateMs.
  if (basis !== undefined && tick.value !== value) {
    const next = { ...tick, value };
    setTick(next);
    return next;
  }
  return tick;
}

function ageText(ms: number, now: number): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  return s < 60 ? `${s}s ago` : formatOpAge(ms, now);
}

function TickCell({
  value,
  stamp,
  windowMs,
  text,
  sub,
  title,
  emphasis = false,
  basis,
}: {
  /** What the direction compares: the side's probability. */
  value: number | null;
  stamp: number | null;
  windowMs: number;
  text: string;
  sub: string | null;
  title?: string;
  emphasis?: boolean;
  /** Changes when the value's makeup changes without a new quote time. */
  basis?: string;
}) {
  const tick = useTick(value, stamp, windowMs, basis);
  return (
    <td className="px-0.5 py-0.5 text-center" title={title}>
      <span
        // Remount per update so the one-shot animation restarts.
        key={basis == null ? stamp ?? 'none' : tick.flash}
        data-tick={stamp != null ? tick.dir : undefined}
        className={cn(
          'inline-block min-w-[3.25rem] rounded px-1 py-0.5 tabular-nums',
          stamp != null && TICK_CLASS[tick.dir],
        )}
        style={stamp != null ? { animationDuration: `${windowMs}ms`, animationDelay: `${tick.delayMs}ms` } : undefined}
      >
        <span className={cn('block text-xs', emphasis ? 'font-bold' : 'font-semibold')}>{text}</span>
        {sub != null && <span className="text-muted-foreground block text-[10px] leading-tight">{sub}</span>}
      </span>
    </td>
  );
}

/** Why a picked book is not in the My books consensus, in the hover. */
const EXCLUSION_TEXT: Record<SelectedBookExclusion, string> = {
  'not-eligible': "not in hydra's census",
  'no-draw-quote': 'no draw price',
  withdrawn: 'withdrawn',
  'no-quote': 'no quote',
  'invalid-quote': 'unusable quote',
  'duplicate-quote': 'duplicate quote',
  'stale-field-outlier': 'older than 5 s and over 20¢ from the field median',
  'older-than-selected': 'older than the five freshest',
  'anchor-aged-and-drifted': 'older than 5 s and away from the field',
};

/** The My books cell's hover: the value's basis, then every picked book —
 *  in with its weight, or out with why. */
function personalConsensusTitle(r: SelectedBookConsensusResult, now: number): string {
  const c = r.consensus;
  const head = c
    ? `My books consensus of ${c.contributingBooks}${c.pinnacleAnchored ? ', Pinnacle-anchored' : ''} — updated ${ageText(c.lastUpdateMs, now)}.`
    : 'No My books consensus: it needs a usable quote on this market from one of your selected books.';
  const used = r.books.filter((b) => b.status === 'contributing')
    .map((b) => `${oddsBookLabel(b.bookmaker)} ×${(b.weight ?? 0).toFixed(1)}`);
  const out = r.books.filter((b) => b.status !== 'contributing').map((b) => {
    const why = b.status === 'withdrawn' && b.withdrawal
      ? opWithdrawReasonLabel(b.withdrawal.reason)
      : EXCLUSION_TEXT[b.status as SelectedBookExclusion];
    return `${oddsBookLabel(b.bookmaker)} (${why})`;
  });
  return [head, used.length > 0 ? `In: ${used.join(', ')}.` : null, out.length > 0 ? `Out: ${out.join(', ')}.` : null,
    'Worked out in this browser from your books only; display only.'].filter(Boolean).join(' ');
}

/** My books / All books — shown only to a trader who picked books. */
function BookViewSwitch({ showAll, onChange }: { showAll: boolean; onChange: (showAll: boolean) => void }) {
  const options: [boolean, string][] = [[false, 'My books'], [true, 'All books']];
  return (
    <div role="group" aria-label="Books shown" className="border-border flex shrink-0 overflow-hidden rounded border text-[10px]">
      {options.map(([all, label]) => (
        <button
          key={label}
          type="button"
          aria-pressed={showAll === all}
          onClick={() => onChange(all)}
          className={cn(
            // 16 px line + 1 px padding + the 1 px border = 20 px: the
            // header's own line height, so the switch never changes it.
            'px-1.5 py-px leading-4 font-medium',
            showAll === all ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

interface Props {
  gameKey: string | null;
  awayCode: string;
  homeCode: string;
  selection: OddsBoardSelection;
}

export function OddsBooksTable({ gameKey, awayCode, homeCode, selection }: Props) {
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  // #1287 — the signed-in trader's own books, if they picked any. With none,
  // `mine` is false and everything below runs exactly as before.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const myBooks = usePersonalBooks((s) => selectOwnedPersonalBooks(s, viewerId));
  const [showAll, setShowAll] = useState(false);
  const mine = myBooks.length > 0 && !showAll;
  const version = useMarketStore((s) => s.version);
  const selectionKey = JSON.stringify(selection);
  // The selected line's slice; while that line has no OddsPapi quotes (or
  // no OddsPapi market at all), the full-game moneyline instead, labelled as
  // such. The page always has the moneyline's consensus and Pinnacle — its
  // Moneyline row shows them — so the table never sits empty waiting on a
  // line OddsPapi may not quote. Never a neighbouring line: a nearby strike
  // is a different contract.
  const { slice, fallback } = useMemo(() => {
    void version;
    if (!gameKey || !showOpOdds) return { slice: null, fallback: false };
    const st = useMarketStore.getState();
    const chosen = JSON.parse(selectionKey) as OddsBoardSelection;
    const selected = resolveOddsBoardSlice(chosen, { key: gameKey, homeCode }, {
      moneyline: st.getOpOddsFor,
      spread: st.getOpSpreadLineFor,
      total: st.getOpTotalLineFor,
      bucket: st.getOpMarketFor,
    });
    if (selected || chosen.kind === 'moneyline') return { slice: selected, fallback: false };
    const moneyline = st.getOpOddsFor(gameKey);
    return { slice: moneyline, fallback: moneyline != null };
  }, [version, gameKey, homeCode, selectionKey, showOpOdds]);

  const title = oddsBoardTitle(fallback ? { kind: 'moneyline' } : selection, { awayCode }, slice);
  const fallbackNote = !fallback ? null : selection.kind === 'unmapped'
    ? 'the selected line has no OddsPapi market — showing the moneyline'
    : `${oddsBoardTitle(selection, { awayCode }, null)} has no OddsPapi quotes yet — showing the moneyline`;
  // Tooltip ages only; the highlights run on CSS time.
  const now = useSharedNow(1000);
  const sides = slice ? oddsBoardSides(slice, { awayCode, homeCode }) : [];
  const consensus = slice?.market.consensus ?? null;
  const withdrawn = new Map((slice?.market.withdrawnBooks ?? []).map((w) => [w.bookmaker, w]));
  const updatedAt = new Map((slice?.market.books ?? []).map((b) => [b.bookmaker, b.updatedAtMs]));
  // Books actually quoting — Pinnacle's column stands even when it is not.
  const quoting = slice?.market.books.length ?? 0;
  // Every cell remounts when the market changes, so a line switch never
  // reads as a tick.
  const marketId = slice
    ? [gameKey, slice.market.marketType, slice.market.period ?? slice.market.periodTypeId, slice.market.points,
      slice.market.stat, slice.market.playerKey].join('|')
    : selectionKey;

  // The field-book slots carry over render to render within one market, so a
  // book keeps its column; a new market lays out fresh. Derived during
  // render (nextFieldBookSlots is idempotent on its own output).
  const [layout, setLayout] = useState<{ id: string; slots: readonly string[] }>({ id: '', slots: [] });
  // My books has no slots: its columns are the selection, fixed.
  const fieldBooks = slice && !mine ? nextFieldBookSlots(layout.id === marketId ? layout.slots : [], slice.market, now) : [];
  if (slice && !mine && (layout.id !== marketId || fieldBooks.join() !== layout.slots.join())) {
    setLayout({ id: marketId, slots: fieldBooks });
  }
  const books = mine ? personalBookColumns(myBooks) : ['pinnacle', ...fieldBooks];
  // Field books the cap leaves out (quoting or withdrawn, Pinnacle aside).
  const hidden = mine ? 0 : new Set([...(slice?.market.books ?? []), ...(slice?.market.withdrawnBooks ?? [])]
    .map((b) => b.bookmaker).filter((b) => b !== 'pinnacle' && !fieldBooks.includes(b))).size;
  // A book outside hydra's reviewed census with no quote on this market at
  // all and no withdrawal entry (either proves hydra delivered it). A book
  // quoting the market but not one side — a 2-way quote in a 3-way market's
  // Draw row — is not "not in census", just no quote for that side. In the
  // All books and default tables every column is a quoting or withdrawn book
  // (or Pinnacle, in the census), so this never fires there.
  const notFed = (b: string) => !OP_CONSENSUS_ELIGIBLE_BOOKS.has(b) && !withdrawn.has(b) && !updatedAt.has(b);
  // #1287 — the My books consensus for this market, at the shared clock's
  // time (the contract's compute instant: weights settle within 6 s of a
  // quote). Only in My books; nobody else computes it.
  const personal = mine && slice ? selectedBookConsensus(slice.market, myBooks, now) : null;
  const personalSwapped = slice != null && oddsBoardSwapped(slice, { homeCode });
  const personalTenths = (slot: 'away' | 'home' | 'draw'): number | null => {
    const c = personal?.consensus;
    if (!c) return null;
    if (slot === 'draw') return c.drawTenths ?? null;
    return (slot === 'away') !== personalSwapped ? c.awayTenths : c.homeTenths;
  };
  const personalTitle = personal ? personalConsensusTitle(personal, now) : '';
  const personalBasis = personal
    ? personal.books.filter((b) => b.status === 'contributing').map((b) => b.bookmaker).join(',')
    : undefined;

  let empty: string | null = null;
  if (!showOpOdds) empty = 'OddsPapi is hidden by the odds filter.';
  else if (!slice || (quoting === 0 && withdrawn.size === 0 && consensus == null)) {
    empty = selection.kind === 'unmapped' ? 'OddsPapi has no market for the selected line.' : 'No OddsPapi quotes for this market yet.';
  }

  // Beside the switch the count may shorten (it is longer, and the switch
  // keeps its size); alone it is exactly the shipped span.
  const withSwitch = myBooks.length > 0 && showOpOdds;
  const countText = mine
    ? `${books.filter((b) => updatedAt.has(b)).length} of ${books.length} my books quoting`
    : `${quoting} ${quoting === 1 ? 'book' : 'books'}`;
  // In My books the header also counts MY books, so hydra's count says whose
  // it is; elsewhere it is exactly the shipped text.
  const consensusCountLabel = consensus == null ? ''
    : mine ? `hydra consensus of ${consensus.contributingBooks}` : `consensus of ${consensus.contributingBooks}`;
  const count = !empty && (
    <span
      className={withSwitch ? 'text-muted-foreground min-w-0 truncate text-[10px]' : 'text-muted-foreground shrink-0 text-[10px]'}
      title={withSwitch ? `${countText}${hidden > 0 ? ` · ${FIELD_BOOK_CAP} most recent shown` : ''}${consensus != null ? ` · ${consensusCountLabel}` : ''}` : undefined}
    >
      {countText}
      {hidden > 0 && ` · ${FIELD_BOOK_CAP} most recent shown`}
      {consensus != null && ` · ${consensusCountLabel}`}
    </span>
  );

  return (
    <section data-testid="odds-books-table">
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="text-foreground text-sm font-semibold">OddsPapi</span>
          <span className="text-muted-foreground shrink-0 text-xs" data-testid="odds-books-title">{title}</span>
          {fallbackNote && (
            <span className="truncate text-[10px] text-amber-300/80" data-testid="odds-books-fallback" title={fallbackNote}>
              {fallbackNote}
            </span>
          )}
        </div>
        {/* With no books picked the header is exactly the shipped one. */}
        {/* flex-1 from a zero basis: the title keeps its width and the count
            shortens into what is left; the switch never shrinks. */}
        {withSwitch ? (
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            {count}
            <BookViewSwitch showAll={showAll} onChange={setShowAll} />
          </div>
        ) : count}
      </div>
      {empty ? (
        <div className="text-muted-foreground px-3 py-4 text-center text-xs">{empty}</div>
      ) : (
        <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
          <table
            className="table-fixed border-collapse border-spacing-0"
            style={{ width: `${LABEL_REM + CONSENSUS_REM + (mine ? CONSENSUS_REM : 0) + books.length * BOOK_REM}rem` }}
          >
            <colgroup>
              <col style={{ width: `${LABEL_REM}rem` }} />
              <col style={{ width: `${CONSENSUS_REM}rem` }} />
              {mine && <col style={{ width: `${CONSENSUS_REM}rem` }} />}
              {books.map((b) => <col key={b} style={{ width: `${BOOK_REM}rem` }} />)}
            </colgroup>
            <thead className="bg-muted/50 border-border border-b">
              <tr className="text-muted-foreground text-[10px] font-semibold uppercase">
                <th className="px-2 py-1 text-left" />
                <th
                  className="text-foreground px-1 py-1 text-center"
                  title="OddsPapi consensus — the de-vigged fair value across the contributing sportsbooks, Pinnacle-weighted when present. Hydra-computed."
                >
                  <span className="block">Consensus</span>
                  <span className="block text-[9px] font-normal normal-case">(de-vigged)</span>
                </th>
                {mine && (
                  <th
                    className="text-foreground px-1 py-1 text-center"
                    title="My books consensus — the de-vigged fair value across only the books you picked, worked out in this browser by a port of hydra's published consensus method (Pinnacle-weighted when present). Not hydra's consensus; display only, never used for orders."
                  >
                    <span className="block">My books</span>
                    <span className="block text-[9px] font-normal normal-case">(de-vigged)</span>
                  </th>
                )}
                {books.map((b) => {
                  const at = updatedAt.get(b);
                  const role = b === 'pinnacle' ? 'Pinnacle — the sharp anchor of the consensus. Quoted price, with vig.' : `${oddsBookLabel(b)} — quoted price, with vig.`;
                  return (
                    <th
                      key={b}
                      className="truncate px-1 py-1 text-center"
                      title={at != null
                        ? `${role} Last update ${ageText(at, now)}.`
                        : notFed(b)
                          ? `${oddsBookLabel(b)} (OddsPapi "${b}") is not in hydra's reviewed OddsPapi census, so hydra is not expected to deliver it. No quote for this market.`
                          : `${role} No quote for this market.`}
                    >
                      <span className="block truncate">{oddsBookLabel(b)}</span>
                      <span
                        // "not in census" is the one header line a trader reads as a fact
                        // about the book, so it carries its own light-theme shade (4.39:1
                        // inherited → about 7:1); the age labels keep the shipped classes.
                        className={cn('block text-[9px] font-normal normal-case tabular-nums', at == null && notFed(b) && 'light:text-gray-600')}
                        data-book-age={b}
                      >
                        {at != null ? quoteAgeLabel(at, now) : notFed(b) ? 'not in census' : '—'}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody key={marketId}>
              {sides.map((side) => (
                <tr key={side.slot} className="border-border/50 border-b last:border-b-0">
                  <td className="text-foreground truncate px-2 py-1 text-xs font-semibold" title={side.label}>{side.label}</td>
                  <TickCell
                    value={side.consensusTenths}
                    stamp={side.consensusTenths != null ? consensus?.lastUpdateMs ?? null : null}
                    windowMs={OP_FIELD_FRESH_MS}
                    text={fairPctText(side.consensusTenths) ?? '—'}
                    sub={fairAmericanText(side.consensusTenths, null)}
                    emphasis
                    title={consensus != null
                      ? `Consensus of ${consensus.contributingBooks} books${consensus.pinnacleAnchored ? ', Pinnacle-anchored' : ''} — updated ${ageText(consensus.lastUpdateMs, now)}`
                      : 'No consensus for this market'}
                  />
                  {mine && (
                    <TickCell
                      value={personalTenths(side.slot)}
                      stamp={personalTenths(side.slot) != null ? personal?.consensus?.lastUpdateMs ?? null : null}
                      basis={personalBasis}
                      windowMs={OP_FIELD_FRESH_MS}
                      text={fairPctText(personalTenths(side.slot)) ?? '—'}
                      sub={fairAmericanText(personalTenths(side.slot), null)}
                      emphasis
                      title={personalTitle}
                    />
                  )}
                  {books.map((b) => {
                    const q = side.quotes.get(b);
                    const w = withdrawn.get(b);
                    if (!q) {
                      return (
                        <td
                          key={b}
                          className="text-muted-foreground px-1 py-1 text-center text-xs"
                          title={w
                            ? `${oddsBookLabel(b)} withdrawn — ${opWithdrawReasonLabel(w.reason)}, ${formatOpAge(w.sinceMs, now)}`
                            : notFed(b)
                              ? `${oddsBookLabel(b)} is not in hydra's OddsPapi census`
                              : mine && updatedAt.has(b)
                                ? `${oddsBookLabel(b)} has no ${side.label} quote for this market`
                                : `${oddsBookLabel(b)} has no quote for this market`}
                        >
                          {w ? <span className="text-[10px] line-through">{opWithdrawReasonLabel(w.reason)}</span> : '—'}
                        </td>
                      );
                    }
                    const american = americanForQuote(q.quote);
                    const implied = decimalToImpliedPctText(q.quote.decimal);
                    return (
                      <TickCell
                        key={b}
                        value={decimalToImpliedPct(q.quote.decimal)}
                        stamp={q.updatedAtMs}
                        windowMs={b === 'pinnacle' ? OP_ANCHOR_GLOW_MS : OP_FIELD_FRESH_MS}
                        text={implied ?? '—'}
                        sub={american === '—' ? null : american}
                        title={`${oddsBookLabel(b)} ${american}${implied ? ` (${implied}% implied, with vig)` : ''} — updated ${ageText(q.updatedAtMs, now)}`}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </EdgeFadeScroller>
      )}
    </section>
  );
}
